import { handleFetch } from "@common/utils/Http";

// ========================
// Proposal Section
// ========================

export interface ProposalDto {
  ProposalId: number;
  ProposalTemplateVersionId: number;
  ProposalTemplateId: number;
  EntityId: number;
  EntityName: string;
  ProposalStatusId: number;
  Name: string;
  Answers: Record<string, unknown>;
  RubricAnswers: Record<string, unknown>;
  Comments: Record<string, ProposalComment[]>;
  ProposalTypeId: number;
  SubmittedDate: string | null;
  FormattedSubmittedDate: string | null;
  CreatedDate: string;
  CreatedUser: string;
  UpdatedDate: string;
  UpdatedUser: string;
}

export interface ProposalComment {
  Message: string;
  UserId: string;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface UpsertProposalCommand {
  ProposalId: number;
  ProposalTemplateVersionId: number;
  EntityId: number;
  ProposalStatusId: number;
  ProposalTypeId: number;
  Name: string;
  Answers: Record<string, any>;
  RubricAnswers: Record<string, any>;
  Comments: Record<string, ProposalComment[]>;
}

// ========================
// ProposalHistory Section
// ========================

export interface ProposalHistoryDto {
  ProposalHistoryId: number;
  ProposalId: number;
  ProposalStatusId: number;
  Answers: string;
  CreatedDate: string;
  CreatedUser: string;
  Proposal: object;
  ProposalStatus: object;
}

export interface UpsertProposalHistoryCommand {
  ProposalHistoryId: number;
  ProposalId: number;
  ProposalStatusId: number;
  Answers: string;
  EntityId: number;
}
// ========================
// ProposalType Section
// ========================

export interface ProposalTypeDto {
  ProposalTypeId: number;
  Name: string;
  Descriptions: string | null;
  CreatedDate: string;
}

// ========================
// ProposalStatus Section
// ========================

export interface ProposalStatusDto {
  ProposalStatusId: number;
  Name: string;
  Descriptions: string | null;
  CreatedDate: string;
}

export class ProposalApiClient {
  private baseURL = "/epp/participants";

  // ========================
  // Proposal Section
  // ========================

  toProposalDto(data: any): ProposalDto {
    return {
      ...data,
      Answers: JSON.parse(data.Answers),
      RubricAnswers: JSON.parse(data.RubricAnswers),
      Comments: data.Comments ? JSON.parse(data.Comments) : {},
    };
  }

  //stringify
  async getAll(entityId: number) {
    const value = await handleFetch<any[]>({
      url: `${this.baseURL}/${entityId}/proposals`,
      method: "GET",
    });

    return value.map(this.toProposalDto);
  }

  async get(entityId: number, proposalId: number) {
    const value = await handleFetch<ProposalDto>({
      url: `${this.baseURL}/${entityId}/proposals/${proposalId}`,
      method: "GET",
    });

    return this.toProposalDto(value);
  }

  async upsertProposal(entityId: number, formData: FormData) {
    const res = await handleFetch<ProposalDto>({
      url: `${this.baseURL}/${entityId}/proposals`,
      method: "POST",
      body: formData,
    });

    return this.toProposalDto(res);
  }

  deleteProposal(entityId: number, proposalId: number) {
    return handleFetch<null>({
      url: `${this.baseURL}/${entityId}/proposals/${proposalId}`,
      method: "DELETE",
    });
  }

  // ========================
  // ProposalHistory Section
  // ========================

  GetProposalHistoriesById(entityId: number, proposalId: number) {
    return handleFetch<ProposalHistoryDto[]>({
      url: `${this.baseURL}/${entityId}/proposals/history/${proposalId}`,
      method: "GET",
    });
  }

  getProposalHistory(entityId: number, proposalHistoryId: number) {
    return handleFetch<ProposalHistoryDto>({
      url: `${this.baseURL}/${entityId}/proposals/history/${proposalHistoryId}`,
      method: "GET",
    });
  }

  upsertProposalHistory(entityId: number, data: UpsertProposalHistoryCommand) {
    return handleFetch<ProposalHistoryDto>({
      url: `${this.baseURL}/${entityId}/proposals/history`,
      method: "POST",
      body: data,
    });
  }

  deleteProposalHistory(entityId: number, proposalHistoryId: number) {
    return handleFetch<null>({
      url: `${this.baseURL}/${entityId}/proposals/history/${proposalHistoryId}`,
      method: "DELETE",
    });
  }

  // ========================
  // ProposalType Section
  // ========================

  getProposalTypes() {
    return handleFetch<ProposalTypeDto[]>({
      url: `${this.baseURL}/proposals/types`,
      method: "GET",
    });
  }

  getProposalType(proposalTypeId: number) {
    return handleFetch<ProposalTypeDto>({
      url: `${this.baseURL}/$proposals/type/${proposalTypeId}`,
      method: "GET",
    });
  }

  // ========================
  // ProposalStatus Section
  // ========================

  getProposalStatuses() {
    return handleFetch<ProposalStatusDto[]>({
      url: `${this.baseURL}/proposals/statuses`,
      method: "GET",
    });
  }

  getProposalStatus(proposalStatusId: number) {
    return handleFetch<ProposalStatusDto>({
      url: `${this.baseURL}/proposals/Status/${proposalStatusId}`,
      method: "GET",
    });
  }
}